import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Search, Heart } from 'lucide-react';
import { getCampaigns, startDonation } from '../../lib/give';
import { centerToast } from '../../components/CenterToast';

const sgd = (cents) => (cents / 100).toLocaleString('en-SG', { maximumFractionDigits: 0 });

export default function Give() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('All');
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState({});   // slug -> amount_cents
  const [custom, setCustom] = useState({});    // slug -> custom $ string
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    getCampaigns().then((c) => { setCampaigns(c); setLoading(false); });
    // Stripe returns the donor here after checkout.
    const p = new URLSearchParams(window.location.search).get('give');
    if (p === 'success') centerToast('Thank you for your generosity. May it be accepted.', '🤲');
    if (p === 'cancel') centerToast('Donation cancelled — no charge was made.', '💛');
    if (p) {
      const url = window.location.pathname + window.location.hash;
      window.history.replaceState(window.history.state, '', url);
    }
  }, []);

  const cats = ['All', ...Array.from(new Set(campaigns.map((c) => c.category).filter(Boolean)))];
  const shown = campaigns.filter((c) =>
    (category === 'All' || c.category === category) &&
    (c.title.toLowerCase().includes(query.toLowerCase()) || (c.blurb || '').toLowerCase().includes(query.toLowerCase())));

  const amountFor = (c) => {
    const cu = Number(custom[c.slug]);
    if (custom[c.slug] && cu > 0) return Math.round(cu * 100);
    return chosen[c.slug] || (Array.isArray(c.suggested) ? c.suggested[1] : 5000);
  };

  const donate = async (c) => {
    const cents = amountFor(c);
    if (cents < (c.min_cents || 500)) {
      centerToast(`Minimum donation is $${sgd(c.min_cents || 500)}`, '💛');
      return;
    }
    setBusy(c.slug);
    const r = await startDonation(c.slug, cents);   // redirects on success
    if (!r.ok) { centerToast(r.error || 'Could not start the donation.', '⚠️'); setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-background page-fade-in">
      <div className="bg-card border-b px-4 py-6">
        <h1 className="text-3xl font-bold mb-2">Give</h1>
        <p className="text-muted-foreground">Zakat, sadaqah and support for MTFA&apos;s causes — 100% channelled to those in need.</p>
      </div>

      <div className="px-4 py-4 bg-card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search causes..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-10 h-11" />
        </div>
      </div>

      <div className="px-4 py-3 bg-card border-b">
        <div className="flex gap-2 overflow-x-auto horizontal-scroll pb-2">
          {cats.map((c) => (
            <Button key={c} variant={category === c ? 'default' : 'outline'} size="sm"
              onClick={() => setCategory(c)} className="whitespace-nowrap capitalize">{c}</Button>
          ))}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {loading && <p className="text-muted-foreground text-sm">Loading causes…</p>}
        {!loading && shown.length === 0 && <p className="text-muted-foreground text-sm">No causes match your search.</p>}
        {shown.map((c) => {
          const pct = c.goal_cents ? Math.min(100, Math.round((c.raised_cents / c.goal_cents) * 100)) : null;
          const suggested = Array.isArray(c.suggested) ? c.suggested : [1000, 3000, 5000, 10000];
          const active = amountFor(c);
          return (
            <Card key={c.slug} className="overflow-hidden card-hover">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-semibold text-lg mb-1">{c.title}</h3>
                    <p className="text-sm text-muted-foreground">{c.blurb}</p>
                  </div>
                  <Badge className="bg-accent capitalize shrink-0">{c.category}</Badge>
                </div>

                {pct !== null && (
                  <div className="space-y-1.5">
                    <Progress value={pct} className="h-2" />
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-bold text-primary">${sgd(c.raised_cents)} raised</span>
                      <span className="text-muted-foreground">of ${sgd(c.goal_cents)}</span>
                    </div>
                  </div>
                )}
                {pct === null && c.raised_cents > 0 && (
                  <div className="text-sm font-bold text-primary">${sgd(c.raised_cents)} raised so far</div>
                )}

                {/* amount picker */}
                <div className="flex flex-wrap gap-2 pt-1">
                  {suggested.map((amt) => (
                    <button key={amt} type="button"
                      onClick={() => { setChosen({ ...chosen, [c.slug]: amt }); setCustom({ ...custom, [c.slug]: '' }); }}
                      className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition ${
                        !custom[c.slug] && active === amt ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border'
                      }`}>${sgd(amt)}</button>
                  ))}
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                    <input inputMode="numeric" placeholder="Other" value={custom[c.slug] || ''}
                      onChange={(e) => setCustom({ ...custom, [c.slug]: e.target.value.replace(/[^0-9]/g, '') })}
                      className="w-24 pl-6 pr-2 py-1.5 rounded-lg text-sm border border-border bg-card focus:outline-none focus:ring-2 focus:ring-primary/40" />
                  </div>
                </div>

                <Button className="w-full gap-2" size="lg" disabled={busy === c.slug} onClick={() => donate(c)}>
                  <Heart className="h-4 w-4" />
                  {busy === c.slug ? 'Redirecting to secure checkout…' : `Donate $${sgd(active)}`}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
